import { useCallback, useEffect, useRef, useState } from 'react';
import { openZendeskWidget, emitUpdateZendeskHelpPosition } from 'web-services/chat';
import { useMatchMedia } from 'web-ui-kit/hooks';
import { QUERY_FOR_PHONE_WIDTH } from 'web-ui-kit/media';

const DEFAULT_RIGHT = 16;

export default function useZendeskHelpButton () {
  const ref = useRef(null);
  const [rightPos, setRightPos] = useState(DEFAULT_RIGHT);
  // We default to true so that the button is not displayed
  // before the help button is clicked
  const [zendeskOpen, setZendeskOpen] = useState(true);

  // Gets the position for the left side of an element
  const getElLeftOffset = useCallback(el => {
    const rect = el.getBoundingClientRect();
    return window.innerWidth - rect.right + rect.width;
  }, []);

  // Gets the target position (left side) of the messenger panel
  const getMessengerPanelTargetX = useCallback(() => {
    const messengerPanel = document.getElementById('messenger-panel');
    if (messengerPanel) {
      return getElLeftOffset(messengerPanel);
    }
    return null;
  }, [getElLeftOffset]);

  // Gets the target position (left side) of the messenger button
  const getMessengerButtonTargetX = useCallback(() => {
    const messengerButton = document.getElementById('messenger-button');
    if (messengerButton) {
      return getElLeftOffset(messengerButton);
    }
    return null;
  }, [getElLeftOffset]);

  // Update the position when the size changes
  const onMatchChanged = useCallback(() => {
    emitUpdateZendeskHelpPosition();
  }, []);

  useMatchMedia({
    queryString: QUERY_FOR_PHONE_WIDTH,
    onChanged: onMatchChanged
  });

  // Updates the position of the help button based on
  // - If the messenger button exists
  // - If the messenger panel exists
  // - There are no messenger elements
  const updatePosition = useCallback(() => {
    if (ref.current) {
      return new Promise(resolve => {
        // We need to wait for a render update
        // so that all elements are in the DOM
        // before checking their position/existence
        window.requestAnimationFrame(() => {
          // Check for the messenger button first
          let position = getMessengerButtonTargetX();
          // Check for the messenger panel next
          if (!position) {
            position = getMessengerPanelTargetX();
          }
          if (!position) {
            // Use default
            position = DEFAULT_RIGHT;
          }
          setRightPos(position);
          resolve(position);
        });
      });
    }
  }, [ref, getMessengerButtonTargetX, getMessengerPanelTargetX, setRightPos]);

  // Action on click of the help button
  const onClick = useCallback(async () => {
    // Wait for the position to be set
    const newPos = await updatePosition();
    // Set Zendesk Open
    setZendeskOpen(true);
    // Open the widget
    openZendeskWidget(newPos);
  }, [setZendeskOpen, rightPos, updatePosition]);

  const onZendeskClosed = useCallback((e) => {
    // Update the position
    updatePosition();
    // Open Zendesk
    setZendeskOpen(false);
  }, [setZendeskOpen, updatePosition]);

  useEffect(() => {
    if (typeof window !== 'undefined' && ref.current) {
      ref.current.addEventListener('zendesk-help-update-position', updatePosition, false);
      ref.current.addEventListener('zendesk-closed', onZendeskClosed, false);
    }
    return () => {
      if (typeof window !== 'undefined' && ref.current) {
        ref.current.removeEventListener('zendesk-help-update-position', updatePosition);
        ref.current.removeEventListener('zendesk-closed', onZendeskClosed);
      }
    };
  }, [ref, onZendeskClosed, updatePosition]);

  return {
    onClick,
    ref,
    rightPos,
    zendeskOpen
  };
}
